import { useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, PlusCircle, Home as HomeIcon, ShieldAlert, Tag, Instagram, Users, ReceiptText } from "lucide-react";

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      navigate("/login?next=/admin");
    }
  }, [user, loading, navigate]);

  if (loading) return <div className="p-10 text-white/50">Loading…</div>;
  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-md mx-auto py-20 px-5 text-center">
        <ShieldAlert size={36} className="text-[color:var(--ufr-green)] mx-auto" />
        <div className="font-display text-2xl text-white mt-4">Admin only</div>
        <p className="text-white/60 text-sm mt-2">You need an admin account to access this area.</p>
      </div>
    );
  }

  const nav = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wider transition-colors border-l-2 ${
      isActive ? "border-[color:var(--ufr-green)] text-[color:var(--ufr-green)] bg-[rgba(34,255,102,0.05)]" : "border-transparent text-white/70 hover:text-white"
    }`;

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-10" data-testid="admin-layout">
      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="ufr-card rounded-md p-2 sticky top-24">
            <div className="px-3 py-3 mb-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Admin</div>
              <div className="font-display text-xl text-white">Control room</div>
            </div>
            <NavLink to="/admin" end className={nav} data-testid="admin-nav-dashboard">
              <LayoutDashboard size={16} /> Dashboard
            </NavLink>
            <NavLink to="/admin/members" className={nav} data-testid="admin-nav-members">
              <Users size={16} /> Members
            </NavLink>
            <NavLink to="/admin/orders" className={nav} data-testid="admin-nav-orders">
              <ReceiptText size={16} /> Orders
            </NavLink>
            <NavLink to="/admin/new" className={nav} data-testid="admin-nav-new">
              <PlusCircle size={16} /> New raffle
            </NavLink>
            <NavLink to="/admin/discounts" className={nav} data-testid="admin-nav-discounts">
              <Tag size={16} /> Discount codes
            </NavLink>
            <NavLink to="/admin/instagram" className={nav} data-testid="admin-nav-instagram">
              <Instagram size={16} /> Instagram
            </NavLink>
            <NavLink to="/admin/tiktok" className={nav} data-testid="admin-nav-tiktok">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.1v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.78.12v-3.2a5.78 5.78 0 0 0-.78-.05A5.79 5.79 0 1 0 15.54 18V9.4a7.38 7.38 0 0 0 4.3 1.38V7.6a4.28 4.28 0 0 1-3.24-1.78Z" />
              </svg> TikTok
            </NavLink>
            <Link to="/" className="flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wider border-l-2 border-transparent text-white/40 hover:text-white">
              <HomeIcon size={16} /> Back to site
            </Link>
          </div>
        </aside>
        <main className="lg:col-span-9 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
